/**
 * readAloud/player — one readout at a time, across the whole app.
 *
 * A module singleton with a `useSyncExternalStore` subscription, the same idiom
 * the delivery ledger uses (services/messageDelivery): the transcript renders
 * hundreds of message rows and each one only needs to know "am I the one being
 * read?", so a single store beats per-row state and makes "one readout at a
 * time" true by construction rather than by convention.
 *
 * WHAT MAKES IT FEEL FAST. The server splits a message into sentence-bounded
 * segments and renders one per call. This player plays segment 0 the moment it
 * arrives and fetches segment 1 WHILE 0 is playing, so a long answer starts
 * speaking in a second or two and never gaps in the middle. Two Audio elements
 * alternate, so the next segment is already buffered when the current one ends.
 *
 * THE iOS UNLOCK IS LOAD-BEARING. Mobile Safari only lets an Audio element play
 * if it was played during a user gesture — and the first fetch happens after the
 * click, which breaks the gesture chain. So both elements are primed with a
 * silent clip synchronously inside the click, before anything is awaited. Take
 * that away and the control works everywhere except a phone, which is where it
 * matters most.
 *
 * Deliberately NOT stopped when the tab is hidden: somebody having a message
 * read to them switches tabs and keeps listening. It stops on a second click,
 * on a thread switch, and when the surface unmounts.
 */

import { fetchReadAloudSegment, type ReadAloudResponse } from './client';
import { cancelDeviceVoice, deviceVoiceAvailable, speakWithDeviceVoice } from './deviceVoice';

export type ReadAloudState = 'idle' | 'loading' | 'playing';

export interface ReadAloudSnapshot {
  /** The message being read, as `${threadId}|${messageId}`, or null. */
  key: string | null;
  state: ReadAloudState;
  /** Which segment is sounding, and how many there are (for the label). */
  segment: number;
  segmentCount: number;
  /** D9 copy from the server for a readout that did not happen. */
  notice: string | null;
  /** The message the notice belongs to, so only that row shows it. */
  noticeKey: string | null;
}

export interface ReadAloudTarget {
  threadId: string;
  messageId: string;
  /** What the reader is looking at: the device-voice text, and the store-miss fallback. */
  text: string;
  language?: string;
}

const IDLE: ReadAloudSnapshot = {
  key: null,
  state: 'idle',
  segment: 0,
  segmentCount: 0,
  notice: null,
  noticeKey: null,
};

/** A 0-sample WAV. Primes an Audio element inside a gesture without a sound. */
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgLsAAAB3AQACABAAZGF0YQAAAAA=';

let snapshot: ReadAloudSnapshot = IDLE;
const listeners = new Set<() => void>();

/** Bumped on every start and every stop; async work from an older run is dropped. */
let generation = 0;

let elements: HTMLAudioElement[] = [];
let unlocked = false;

function publish(next: Partial<ReadAloudSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* a bad subscriber must not stop playback */
    }
  });
}

export function subscribeReadAloud(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function readAloudSnapshot(): ReadAloudSnapshot {
  return snapshot;
}

export function readAloudKey(threadId: string | null | undefined, messageId: string): string {
  return `${threadId || ''}|${messageId}`;
}

function audioElements(): HTMLAudioElement[] {
  if (typeof document === 'undefined') return [];
  if (elements.length === 0) {
    elements = [new Audio(), new Audio()];
    for (const el of elements) {
      el.preload = 'auto';
      // Keeps iOS from taking the readout fullscreen or over the video surface.
      el.setAttribute('playsinline', 'true');
    }
  }
  return elements;
}

/**
 * Prime both elements inside the click. Called synchronously from `toggle`,
 * before any await — that is the whole point.
 */
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  for (const el of audioElements()) {
    try {
      el.src = SILENT_WAV;
      const p = el.play();
      if (p && typeof p.then === 'function') p.then(() => el.pause()).catch(() => undefined);
    } catch {
      /* an element that refuses to prime still tries to play later */
    }
  }
}

function halt(): void {
  cancelDeviceVoice();
  for (const el of elements) {
    try {
      el.pause();
      // Drop the source too: a paused element keeps downloading the rest.
      el.removeAttribute('src');
      el.load();
    } catch {
      /* already gone */
    }
  }
}

export function stopReadAloud(): void {
  generation += 1;
  halt();
  publish({ key: null, state: 'idle', segment: 0, segmentCount: 0 });
}

/**
 * A thread switch ends a readout that belonged to the thread being left.
 *
 * A missing id stops NOTHING: the host calls this with the previous thread,
 * which is null on the first render, and a readout must not be cancelled by the
 * surface simply mounting.
 */
export function stopReadAloudForThread(threadId: string | null | undefined): void {
  const id = String(threadId || '').trim();
  if (!id || !snapshot.key) return;
  if (snapshot.key.startsWith(`${id}|`)) stopReadAloud();
}

export function dismissReadAloudNotice(): void {
  if (snapshot.notice === null && snapshot.noticeKey === null) return;
  publish({ notice: null, noticeKey: null });
}

/** Play one URL on the given element and resolve when it finishes or fails. */
function playUrl(el: HTMLAudioElement, url: string): Promise<'ended' | 'failed'> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (how: 'ended' | 'failed') => {
      if (settled) return;
      settled = true;
      el.onended = null;
      el.onerror = null;
      resolve(how);
    };
    el.onended = () => finish('ended');
    el.onerror = () => finish('failed');
    try {
      el.src = url;
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => finish('failed'));
    } catch {
      finish('failed');
    }
  });
}

/**
 * A refusal the SERVER made on purpose — no permission, the daily ceiling, an
 * empty wallet, nothing to read. These are answers, so the reader is told; the
 * device voice is not wheeled out to paper over them.
 */
function isDeliberateRefusal(reason: string | undefined): boolean {
  return reason === 'no-permission'
    || reason === 'not-a-participant'
    || reason === 'ceiling'
    || reason === 'out-of-work-units'
    || reason === 'no-text'
    || reason === 'disabled';
}

/**
 * Start, or stop if this message is already being read.
 *
 * MUST be called from the click handler itself (it primes audio synchronously).
 */
export function toggleReadAloud(target: ReadAloudTarget): void {
  const key = readAloudKey(target.threadId, target.messageId);
  if (snapshot.key === key) {
    stopReadAloud();
    return;
  }
  unlock();
  generation += 1;
  const run = generation;
  halt();
  publish({ key, state: 'loading', segment: 0, segmentCount: 0, notice: null, noticeKey: null });
  void drive(run, key, target);
}

async function drive(run: number, key: string, target: ReadAloudTarget): Promise<void> {
  const els = audioElements();
  let index = 0;
  let total = 1;
  // The fetch for the NEXT segment, started while the current one plays.
  let ahead: Promise<ReadAloudResponse> | null = null;

  const request = (segment: number) => fetchReadAloudSegment({
    threadId: target.threadId,
    messageId: target.messageId,
    segment,
    fallbackText: target.text.slice(0, 2000),
    language: target.language,
  });

  let response = await request(0);
  if (run !== generation) return;

  if (!response.ok || !response.segment) {
    if (isDeliberateRefusal(response.reason)) {
      publish({ key: null, state: 'idle', notice: response.notice || null, noticeKey: key });
      return;
    }
    // The voice could not be had. Rather than dead-end, read it here.
    if (deviceVoiceAvailable() && target.text.trim()) {
      publish({ state: 'playing', segmentCount: 1 });
      await speakWithDeviceVoice(target.text, target.language);
      if (run !== generation) return;
      publish({ key: null, state: 'idle', segment: 0, segmentCount: 0 });
      return;
    }
    publish({ key: null, state: 'idle', notice: response.notice || null, noticeKey: key });
    return;
  }

  total = Math.max(1, response.segmentCount || 1);
  publish({ state: 'playing', segment: 0, segmentCount: total });

  while (run === generation && response.ok && response.segment) {
    const el = els[index % els.length];
    // Start the next fetch BEFORE waiting on this segment to finish playing —
    // this overlap is what keeps a long readout gapless.
    ahead = index + 1 < total ? request(index + 1) : null;
    publish({ segment: index, segmentCount: total });
    const how = await playUrl(el, response.segment.url);
    if (run !== generation) return;
    if (how === 'failed') break;
    if (!ahead) break;
    response = await ahead;
    if (run !== generation) return;
    index += 1;
  }

  if (run !== generation) return;
  publish({ key: null, state: 'idle', segment: 0, segmentCount: 0 });
}

/** Test seam: forget the elements and the unlock between cases. */
export function resetReadAloudPlayerForTests(): void {
  generation += 1;
  elements = [];
  unlocked = false;
  snapshot = IDLE;
  listeners.clear();
}
